import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_INTEGRATIONS_MARKETPLACE_HISTORY_LOGS } from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import {
  Dependencies,
  HistoryLogEntry,
  HistoryLogEntryErrorCode,
  HistoryLogEntrySeverity,
  HistoryLogEvents
} from '../types';
import {
  IntegrationsMarketplaceHistoryLogsController
} from "../controller/IntegrationsMarketplaceHistoryLogsController";
import {dateFormatter} from "@nirvana/shared/time";

@customElement(WEB_COMPONENT_INTEGRATIONS_MARKETPLACE_HISTORY_LOGS)
export class IntegrationsMarketplaceHistoryLogsWebComponent extends LitElement {
  private controller: IntegrationsMarketplaceHistoryLogsController;
  private dependencies: Dependencies;

  constructor(dependencies) {
    super();
    this.dependencies = dependencies;
    this.controller = new IntegrationsMarketplaceHistoryLogsController(this, dependencies);
  }

  static get styles() {
    return styles;
  }

  @property({ type: Array })
  logs: HistoryLogEntry[] = [];

  @property({ type: Boolean })
  isLoading = true;

  @property({ type: String })
  integrationId = '';

  private readonly severityTranslations: Record<string, string> = {
    [HistoryLogEntrySeverity.INFO]: translateFn('Info'),
    [HistoryLogEntrySeverity.WARNING]: translateFn('Warning'),
    [HistoryLogEntrySeverity.ERROR]: translateFn('Error'),
  };

  render(): TemplateResult {
    return html`
      <div class="log-entries-list">
        <div class="log-entries-list--header">
          <gz-title fontSize="16" fontWeight="500">${translateFn('History logs')}</gz-title>
        </div>
        <div class="log-entries-list--content">
          ${this.logs.length > 0 && !this.isLoading ? this.renderLogs() : this.renderEmptyTemplate()}

          <div class="log-entries-list--loader">
            ${this.isLoading
              ? html`<gz-progress-spinner indeterminate spinnerWidth="medium"></gz-progress-spinner>`
              : nothing}
          </div>
        </div>
        <div class="log-entries-list--footer">
          <gz-button @click="${() => this.dispatchEvent(new CustomEvent(HistoryLogEvents.CLOSE))}"
            >${translateFn('Close')}</gz-button
          >
          <gz-button
            @click="${() => this.dispatchEvent(new CustomEvent(HistoryLogEvents.CLEAR_LOGS))}"
            ?disabled="${this.logs.length === 0 || this.isLoading}"
            type="outline"
            color="negative"
            >${translateFn('Clear logs')}</gz-button
          >
        </div>
      </div>
    `;
  }

  private renderEmptyTemplate(): TemplateResult | undefined {
    if (!this.isLoading) {
      return html`
      <gz-image-repo state="empty">
        <gz-title class="image-repo-title" fontWeight="500" fontSize="20"
          >${translateFn('No history logs available.')}</gz-title
        >
      </gz-image-repo>
    `;
    }
  }

  private getSeverityIcon(severity: HistoryLogEntrySeverity): TemplateResult {
    switch (severity) {
      case HistoryLogEntrySeverity.INFO:
        return html`<gz-severity-icon level="unknown"></gz-severity-icon>`;
      case HistoryLogEntrySeverity.WARNING:
        return html`<gz-severity-icon level="medium"></gz-severity-icon>`;
      case HistoryLogEntrySeverity.ERROR:
        return html`<gz-severity-icon level="high"></gz-severity-icon>`;
      default:
        return html`<gz-severity-icon level="unknown"></gz-severity-icon>`;
    }
  }

  private renderLogEntry(logEntry: HistoryLogEntry): TemplateResult {
    return html`
      <div class="log-entry">
        <gz-title fontSize="11" fontWeight="600" class="log-entry--message"
          >${this.renderError(logEntry.errorCode).toUpperCase()}</gz-title
        >

        <div class="log-entry--content">
          <div class="log-entry--row">
            <div class="log-entry--row__label">
              <gz-tooltip isEllipsis tooltipText="${translateFn('Date')}">
                <gz-text fontSize="14">${translateFn('Date')}</gz-text>
              </gz-tooltip>
            </div>
            <div class="log-entry--row__value">
              <gz-tooltip isEllipsis tooltipText="${dateFormatter(logEntry.created, this.dependencies.getTimezone(), 'D MMM YYYY, HH:mm')}">
                <gz-text fontSize="14">${dateFormatter(logEntry.created, this.dependencies.getTimezone(), 'D MMM YYYY, HH:mm')}</gz-text>
              </gz-tooltip>
            </div>
          </div>
          <div class="log-entry--row">
            <div class="log-entry--row__label">
              <gz-tooltip isEllipsis tooltipText="${translateFn('Endpoint name')}"
                ><gz-text fontSize="14">${translateFn('Endpoint name')}</gz-text>
              </gz-tooltip>
            </div>
            <div class="log-entry--row__value">
              <gz-tooltip isEllipsis tooltipText="${logEntry?.specifics?.integratorName ?? translateFn('N/A')}">
                <gz-text fontSize="14">${logEntry?.specifics?.integratorName ?? translateFn('N/A')}</gz-text>
              </gz-tooltip>
            </div>
          </div>
          <div class="log-entry--row">
            <div class="log-entry--row__label">
              <gz-tooltip isEllipsis tooltipText="${translateFn('Severity')}">
                <gz-text fontSize="14">${translateFn('Severity')}</gz-text>
              </gz-tooltip>
            </div>
            <div class="log-entry--row__value">
              ${this.getSeverityIcon(logEntry.severity)}
              <gz-tooltip isEllipsis tooltipText="${this.severityTranslations[logEntry.severity]}">
                <gz-text fontSize="14">${this.severityTranslations[logEntry.severity]}</gz-text>
              </gz-tooltip>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderLogs(): TemplateResult {
    return html`
      <div class="logs">
        ${repeat(
          this.logs,
          (item: HistoryLogEntry) => item._id,
          item => this.renderLogEntry(item)
        )}
      </div>
    `;
  }

  private renderError(value): string {
    switch (value) {
      case HistoryLogEntryErrorCode.OK:
        return translateFn('The connection to the integration has been restored.');
      case HistoryLogEntryErrorCode.INTEGRATION_NOT_ENABLED:
        return translateFn('The integration stopped and can no longer sync. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.LICENSE_REQUIRED:
        return translateFn('A valid license is required to use this feature. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.INTEGRATOR_ENDPOINT_NOT_FOUND:
        return translateFn(
          'The BEST agent used as an integrator cannot be found or no longer exists. (code: {NUMBER})',
          { NUMBER: value }
        );
      case HistoryLogEntryErrorCode.DUPLICATE_INTEGRATION:
        return translateFn('The integration is a duplicate and cannot be synchronized. (code: {NUMBER})', {
          NUMBER: value,
        });
      case HistoryLogEntryErrorCode.INTEGRATION_REMOVED:
        return translateFn('The integration has been stopped. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.MAX_INTEGRATORS_RUNNING:
        return translateFn('The maximum number of integrations has been reached. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.PROVIDER_CONNECTION_FAILED:
        return translateFn('A connection issue with the integrated system has occurred. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.UNSUPPORTED_API_VERSION:
        return translateFn("The system you're integrating with uses an unsupported API version. (code: {NUMBER})", {
          NUMBER: value,
        });
      case HistoryLogEntryErrorCode.LOGIN_FAILED:
        return translateFn('The integration credentials are invalid. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.NO_INTEGRATOR:
        return translateFn('The integration is no longer linked to an integrator. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.INTEGRATOR_OFFLINE:
        return translateFn('The connection to the integrator has been lost. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.INTEGRATION_INSTANCE_MISMATCH:
        return translateFn('Integration instance mismatch. (code: {NUMBER})', { NUMBER: value });
      case HistoryLogEntryErrorCode.PROVIDER_CERTIFICATE_VERIFICATION_FAILED:
        return translateFn("The integration's certificate could not be verified. (code: {NUMBER})", { NUMBER: value });
      case HistoryLogEntryErrorCode.SERVICE_CONNECTION_FAILED:
        return translateFn('The GravityZone service or proxy connection has failed. (code: {NUMBER})', {
          NUMBER: value,
        });
      case HistoryLogEntryErrorCode.SERVICE_HOST_RESOLVE:
        return translateFn('The GravityZone service host or proxy name resolution has failed. (code: {NUMBER})', {
          NUMBER: value,
        });
      case HistoryLogEntryErrorCode.SERVICE_CONNECTION_TIMEOUT:
        return translateFn('Connection to the GravityZone service has timed out. (code: {NUMBER})', { NUMBER: value });
      default:
        return translateFn('An unexpected error with your integration has occurred. Contact Technical Support. (code: {NUMBER})', {
          NUMBER: value,
        });
    }
  }
}
